import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/lib/api/auth';
import { adminApi } from '@/lib/api/admin';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { Info } from '@/design-system/Info';

/** dd/mm/aaaa hh:mm — a trilha precisa da hora, não só da data. */
const carimbo = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

export function AuditPage() {
  const token = useAuthStore((s) => s.token) || '';
  const [busca, setBusca] = useState('');
  const [acaoFiltro, setAcaoFiltro] = useState('');

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['auditoria'],
    queryFn: () => authApi.getAuditoria(token),
    enabled: !!token,
  });

  // Os logs guardam o id do autor; a lista de usuários dá o nome
  const { data: usuarios = [] } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => adminApi.listUsuarios(token),
    enabled: !!token,
  });

  const nomeDoAutor = useMemo(() => {
    const mapa = new Map(usuarios.map((u) => [u.id, u.nome]));
    return (id: string) => mapa.get(id) || id;
  }, [usuarios]);

  const acoes = [...new Set(logs.map((l) => l.acao))].sort();

  const filtrados = logs.filter((l) => {
    if (acaoFiltro && l.acao !== acaoFiltro) return false;
    if (!busca) return true;
    const alvo = `${l.acao} ${l.objeto || ''} ${nomeDoAutor(l.actor_id)}`;
    return alvo.toLowerCase().includes(busca.toLowerCase());
  });

  const porModulo = (acao: string) => acao.split('.')[0] || 'core';
  const modulos = [...new Set(logs.map((l) => porModulo(l.acao)))];

  return (
    <div className="page" id="p-auditoria">
      <div className="head">
        <div>
          <h1>Auditoria</h1>
          <p>
            {/*
              proto:785 diz "log append-only" e "toda rota declara intenção de
              auditoria ou isenção explícita (ISENTA)". A segunda metade é
              literal e verdadeira: `@Audita` / `@AuditoriaIsenta` são
              classificados na subida por `cobertura-auditoria.ts`, e a
              aplicação não sobe com rota que não declarou nada. A primeira
              volta como "append-only", não como "imutável" — ver a nota abaixo.
            */}
            Trilha append-only: cada escrita registra quem fez, o que fez, sobre qual objeto e
            quando, gravada na mesma transação da operação auditada. Toda rota da API declara a ação
            que audita ou uma isenção explícita, conferida na subida da aplicação.
          </p>
        </div>
        <div className="act">
          <button
            className="btn"
            onClick={() => toast('Exportação assinada gerada')}
          >
            Exportar
          </button>
        </div>
      </div>

      {/*
        NOTA DE MÉTODO.

        "Imutável" era forte demais para o que o banco garante. Append-only aqui
        é disciplina do código: só `registrarAuditoria()` escreve em
        `core.audit`, e não há UPDATE, DELETE nem rota que altere a trilha —
        mas a migration cria a tabela sem trigger, sem rule e sem REVOKE, então
        nada impede uma migration futura ou um SQL direto de reescrevê-la. O
        `payload_hash` SHA-256 detecta a adulteração de UMA entrada; ele não é
        encadeado com a anterior, então não detecta remoção nem reescrita em
        bloco.
      */}
      <div className="note warn" data-testid="nota-append-only" style={{ marginBottom: 12 }}>
        <b>Append-only por disciplina, não por constraint.</b> Nenhuma rota altera ou apaga a
        trilha, mas o banco não impede a alteração.
        <Info titulo="Até onde vai a garantia de append-only">
          Não há trigger nem revogação de UPDATE/DELETE em{' '}
          <span className="mono">core.audit</span>: só a disciplina do código mantém a tabela
          somente de leitura e inserção. O hash de cada linha acusa a adulteração de uma entrada
          isolada, não a remoção de linhas nem a reescrita em bloco.
        </Info>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Eventos registrados</div>
          <div className="v">{logs.length}</div>
          <div className="d">últimos 50 do mandato</div>
        </div>
        <div className="card kpi">
          <div className="l">Tipos de ação</div>
          <div className="v">{acoes.length}</div>
          <div className="d">operações distintas auditadas</div>
        </div>
        <div className="card kpi">
          <div className="l">Módulos envolvidos</div>
          <div className="v">{modulos.length}</div>
          <div className="d">{modulos.join(' · ') || '—'}</div>
        </div>
        <div className="card kpi">
          <div className="l">Autores</div>
          <div className="v">
            {new Set(logs.map((l) => l.actor_id)).size}
          </div>
          <div className="d">usuários com escrita registrada</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters">
        <input
          className="in"
          style={{ width: 300 }}
          placeholder="Buscar por ação, objeto ou autor..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select className="sel" value={acaoFiltro} onChange={(e) => setAcaoFiltro(e.target.value)}>
          <option value="">Todas as ações</option>
          {acoes.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </div>

      {/* Tabela */}
      <div className="card flat tw">
        <table>
          <thead>
            <tr>
              <th>Quando</th>
              <th>
                Autor
                <Info titulo="Por que o autor continua identificável">
                  Usuários são desativados, nunca apagados, para que o autor de cada registro
                  continue identificável.
                </Info>
              </th>
              <th>Ação</th>
              <th>Objeto</th>
              <th>Origem</th>
              <th>Intenção</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.map((l) => (
              <tr key={l.id} className="row">
                <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                  {carimbo(l.ts)}
                </td>
                <td>{nomeDoAutor(l.actor_id)}</td>
                <td>
                  <span className="chip neutral mono" style={{ fontSize: 11 }}>
                    {l.acao}
                  </span>
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {l.objeto || '—'}
                </td>
                <td>
                  <span className="chip neutral">{l.origem || 'api'}</span>
                </td>
                <td>
                  <span className={`chip ${l.isenta ? 'neutral' : 'acc'}`}>
                    {l.isenta ? 'ISENTA' : 'AUDITA'}
                  </span>
                </td>
              </tr>
            ))}

            {isLoading && (
              <tr>
                <td colSpan={6} className="empty">
                  Carregando a trilha...
                </td>
              </tr>
            )}
            {!isLoading && filtrados.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  {logs.length === 0
                    ? 'Nenhum evento registrado ainda.'
                    : 'Nenhum evento com os filtros aplicados.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
